import { modelLabel } from "@mfa/v5-contracts";
import { type ReactNode, useEffect, useRef } from "react";
import { FormattedMessage, type IntlShape, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { formatProfileKey } from "../console/profile-labels";
import { ApiError } from "../core/api-client";
import { type MessageId, siteSuffix, suffixedMessageId } from "../core/messages";
import styles from "./Shared.module.css";

/** What can take focus inside a dialog (the backdrop and anything at tabindex -1 excepted). */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex], [contenteditable="true"]';

function focusablesIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.tabIndex >= 0 && !element.closest("[inert], [aria-hidden='true']"),
  );
}

/**
 * Fixed full-screen modal scaffold: a flex-centering container over a click-to-close
 * backdrop button. The dialog itself is the caller's children, rendered above the backdrop
 * in DOM order. While it is open it behaves as a modal dialog should
 * (workflows/V5-Template-Method-Review.md V8): Escape closes it, focus moves into it (to the
 * first control, unless a child already took focus), Tab and Shift+Tab cycle inside it, and
 * closing returns focus to whatever had it before. The backdrop is a pointer target only,
 * out of the tab order and the accessibility tree: keyboard and screen-reader users close
 * with Escape or the dialog's own close button.
 */
export function ModalOverlay({
  ariaCloseLabel,
  onClose,
  zIndex,
  children,
}: {
  ariaCloseLabel: string;
  onClose: () => void;
  zIndex: number;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  // A dialog whose content arrives after it opens (a detail still loading) held focus on the
  // overlay itself; once a control exists, the first one takes it.
  useEffect(() => {
    const container = containerRef.current;
    if (container && document.activeElement === container) focusablesIn(container)[0]?.focus();
  });
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () => focusablesIn(container);
    if (!container.contains(document.activeElement)) (focusables()[0] ?? container).focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        container.focus();
        return;
      }
      const outside = !container.contains(active) || active === container;
      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, []);
  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      data-modal-overlay=""
      style={{
        position: "fixed",
        inset: 0,
        zIndex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(12px, 3vw, 24px)",
        outline: "none",
      }}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        aria-label={ariaCloseLabel}
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          border: 0,
          padding: 0,
          background: "rgba(34,30,69,.55)",
          cursor: "pointer",
        }}
      />
      {children}
    </div>
  );
}

export function BrandMark({ label }: { label: string }) {
  return (
    <span className={styles.brandMark} role="img" aria-label={label}>
      <span />
      <span />
    </span>
  );
}

export function LoadingState() {
  return (
    <div className={styles.centered}>
      <div className={styles.stateCard} role="status" aria-live="polite">
        <div className={styles.spinner} />
        <p>
          <FormattedMessage id="common.loading" />
        </p>
      </div>
    </div>
  );
}

export function ErrorState({
  titleId = "error.backendTitle",
  bodyId = "error.backendBody",
  onRetry,
}: {
  titleId?: MessageId;
  bodyId?: MessageId;
  onRetry?: () => void;
}) {
  return (
    <div className={styles.centered}>
      <section className={styles.stateCard} role="alert">
        <BrandMark label="Agent studio" />
        <h1>
          <FormattedMessage id={titleId} />
        </h1>
        <p>
          <FormattedMessage id={bodyId} />
        </p>
        {onRetry ? (
          <button className={styles.button} onClick={onRetry} type="button">
            <FormattedMessage id="common.retry" />
          </button>
        ) : null}
      </section>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className={styles.empty}>{children}</div>;
}

/** Errors that resolve by picking a bigger plan get a contextual link to /billing. */
const PLAN_UPSELL_CODES = new Set(["credit_cap_reached"]);

/**
 * Kid-facing rule: friendly labels, never raw keys. A `profileKey` param
 * ("foundry/gpt-5-4-mini") renders as "GPT-5.4 Mini · runs on Microsoft
 * Foundry" and a `model` param as its catalog label, before the ICU copy
 * interpolates them.
 */
function friendlyErrorParams(
  intl: IntlShape,
  params: Record<string, string | number>,
): Record<string, string | number> {
  const out = { ...params };
  if (typeof out.profileKey === "string" && out.profileKey) {
    out.profileKey = formatProfileKey(intl, out.profileKey);
  }
  if (typeof out.model === "string" && out.model) out.model = modelLabel(out.model);
  return out;
}

export function InlineApiError({ error }: { error: unknown }) {
  const intl = useIntl();
  if (!error) return null;
  if (error instanceof ApiError) {
    // Per-site error copy where a suffixed pair exists (e.g.
    // error.plan_profile_cap_exceeded.{mfa,lya}); every other code keeps its
    // shared base id.
    const knownId = suffixedMessageId(`error.${error.code}`, siteSuffix()) as MessageId;
    const known = intl.messages[knownId];
    return (
      <p className={styles.errorText} role="alert">
        {known
          ? intl.formatMessage(
              { id: knownId },
              // ICU `select` arguments must always be present: default the
              // common profile-error keys so a missing param never breaks copy.
              {
                code: "other",
                profileKey: "",
                model: "",
                ...friendlyErrorParams(intl, error.params as Record<string, string | number>),
              },
            )
          : intl.formatMessage({ id: "error.generic" }, { traceId: error.traceId ?? error.code })}
        {PLAN_UPSELL_CODES.has(error.code) ? (
          <>
            {" "}
            <Link to="/billing">
              <FormattedMessage id="error.seePlans" />
            </Link>
          </>
        ) : null}
      </p>
    );
  }
  return (
    <p className={styles.errorText} role="alert">
      {intl.formatMessage({ id: "error.generic" }, { traceId: "client" })}
    </p>
  );
}

export const sharedStyles = styles;
